import React, { useState, useEffect, useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import WavesIcon from '@material-ui/icons/Waves';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import orderBy from 'lodash/orderBy';
import Papa from 'papaparse';
import { styles } from './style';
import FloodForecastMap from './FloodforecastMap/FloodForecastMap';
import FloodforecastSidebar from './FloodforecastMap/FloodforecastSidebar/FloodforecastSidebar';
import dayjs from 'dayjs';
import 'dayjs/locale/th';

const useStyles = makeStyles(styles);
const initPath = process.env.REACT_APP_THAIWATER_URL;
const initFewsUrl = process.env.REACT_APP_FEWS_URL;

export default function FloodForecast({ match }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [datas, setDatas] = useState([]);
  const [datasDischarge, setDatasDischarge] = useState([]);
  const [map, setMap] = useState(null);
  const [markers] = useState([]);
  const [zoom] = useState(6);
  const [location] = useState([13.8, 100.55]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ header: '', content: null });
  const [showProvinceBoundary, setShowProvinceBoundary] = useState(false);
  const [showAmphoeBoundary, setShowAmphoeBoundary] = useState(false);
  const [showTambonBoundary, setShowTambonBoundary] = useState(false);
  const [rainfallEnabled, setRainfallEnabled] = useState(false);
  const [forecastDay, setForecastDay] = useState(1);
  const [dataRainfall24, setDataRainfall24] = useState([]);
  const [datasRainToday, setDataRainToday] = useState([]);
  const [datasRainYesterday, setDataRainYesterday] = useState([]);
  const [datasRain3day, setDatasRain3day] = useState([]);
  const [datasRain7day, setDatasRain7day] = useState([]);
  const [datasRain15Day, setDatasRain15Day] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [forecastRainEnabled, setForecastRainEnabled] = useState(false);
  const [forecastRainDay, setForecastRainDay] = useState(1);
  const [datasRain30Day, setDatasRain30Day] = useState([]);
  const handleSetMap = (mapInstance) => setMap(mapInstance);

  const forecastDayToType = {
    1: 'rainfallYesterday',
    2: 'rainfallToday',
    3: 'rainfall24h',
    4: 'rainfall3day',
    5: 'rainfall7day',
    6: 'rainfall15day',
    7: 'rainfall30day',
  };

  const numberOfDays = 6;

  const thaiFormattedDate = (date) => {
    const d = dayjs(date).locale('th');
    return d.format('D MMM') + ` ${d.year() + 543}`;
  };

  const forecastOptions = useMemo(() => 
    Array.from({ length: 6 }, (_, index) => ({
      value: index + 1,
      label: dayjs().add(index, 'day').format('YYYY-MM-DD (07:00)'),
    }))
  , []);

  const rainfallType = forecastDayToType[forecastDay] ?? 'rainfall24h';

  useEffect(() => {
    if (map) {
      const timer = setTimeout(() => {
        map.invalidateSize();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [sidebarOpen, map]);

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL;

    fetch(`${API}/public/rain_24h`)
      .then(r => r.json()).then(res => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 2);
        const sorted = orderBy(res.data, ['rain_24h'], ['desc']);
        setDataRainfall24(sorted.filter(d =>
          new Date(d.rainfall_datetime.replace(/-/g, '/')).getTime() > cutoff && d.rain_24h > 0
        ));
      });

    fetch(`${API}/public/rain_today`)
      .then(r => r.json()).then(res => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 2);
        const sorted = orderBy(res.data, ['rainfall_value'], ['desc']);
        setDataRainToday(sorted.filter(d =>
          new Date(d.rainfall_datetime.replace(/-/g, '/')).getTime() > cutoff && d.rainfall_value > 0
        ));
      });

    fetch(`${API}/public/rain_yesterday`)
      .then(r => r.json()).then(res => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 2);
        const sorted = orderBy(res.data, ['rainfall_value'], ['desc']);
        setDataRainYesterday(sorted.filter(d =>
          new Date(d.rainfall_datetime.replace(/-/g, '/')).getTime() > cutoff && d.rainfall_value > 0
        ));
      });

    fetch(`${API}/provinces/rain3d`)
      .then(r => r.json()).then(res => {
        setDatasRain3day(orderBy(res.data, ['rain_3d'], ['desc']).filter(d => d.rain_3d > 0));
      });

    fetch(`${API}/provinces/rain7d`)
      .then(r => r.json()).then(res => {
        setDatasRain7day(res.data.filter(d => d.rain_7d > 0));
      });

    fetch(`${API}/provinces/rain15d`)
      .then(res => res.json())
      .then(result => {
        // console.log('rain15d result:', result);
        
        if (!result.data) {
          console.error('rain15d: ไม่มี data');
          return;
        }
        
        const sorted = orderBy(result.data, ['rain_15d'], ['desc']);
        const filtered = sorted.filter(d => d.rain_15d >= 0.1);
        setDatasRain15Day(filtered);
      })
      .catch(err => console.error('rain15d error:', err));
    fetch(`${API}/public/rain_monthly`)
      .then(res => res.json())
      .then(result => {
        if (!result.data) return;
        const sorted = orderBy(result.data, ['rainfall_value'], ['desc']);
        setDatasRain30Day(sorted.filter(d => d.rainfall_value >= 0.1));
      })
      .catch(err => console.error('rain_monthly error:', err));
  }, []);

  useEffect(() => {
    fetch(`${initPath}/proxy/csv.php?file=${initFewsUrl}/model-output/data_portal/metadata/hii_waterlevel.csv`)
      .then(res => res.text())
      .then(result => {
        const rawData = Papa.parse(result).data.slice(1);
        setDatas(rawData);
      })
      .catch(err => console.error('fetch waterlevel error:', err));

    fetch(`${initPath}/proxy/csv.php?file=${initFewsUrl}/model-output/data_portal/metadata/rid_discharge.csv`)
      .then(res => res.text())
      .then(result => {
        const rawDischarge = Papa.parse(result).data.slice(1);
        setDatasDischarge(rawDischarge);
      })
      .catch(err => console.error('fetch discharge error:', err));
  }, []);

  const handleModalOpen = (params) => {
    setModalContent(params);
    setModalOpen(true);
  };

  const [layers, setLayers] = useState({
    waterLevel: true,
    discharge: true,
    basin: true,
    river: true,
    provinceBoundary: false,
    amphoeBoundary: false,
    tambonBoundary: false,
  });

  const handleModalClose = () => setModalOpen(false);

  return (
    <Box>
      {/* Title */}
      <Box display="flex" alignItems="center" mb={2}>
        <Box
          display="flex"
          className={classes.shapeCircle}
          alignItems="center"
          justifyContent="center"
        >
          <WavesIcon style={{ color: 'white' }} />
        </Box>
        <Box p={1}>
          <Typography variant="h6" className={classes.titlePage}>
            {t('ระบบคาดการณ์น้ำท่า-ระดับน้ำในแม่น้ำสายหลัก')}
          </Typography>
        </Box>
      </Box>

      {/* Map */}
      <Box
        mt={2}
        mb={2}
        display="flex"
        style={{
          width: '100%',
          height: 'calc(100vh - 150px)',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar */}
        <FloodforecastSidebar
          layers={layers}
          setLayers={setLayers}
          showProvinceBoundary={showProvinceBoundary}
          setShowProvinceBoundary={setShowProvinceBoundary}
          showAmphoeBoundary={showAmphoeBoundary}
          setShowAmphoeBoundary={setShowAmphoeBoundary}
          showTambonBoundary={showTambonBoundary}
          setShowTambonBoundary={setShowTambonBoundary}
          rainfallEnabled={rainfallEnabled}
          setRainfallEnabled={setRainfallEnabled}
          forecastDay={forecastDay}
          setForecastDay={setForecastDay}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          forecastRainEnabled={forecastRainEnabled}
          setForecastRainEnabled={setForecastRainEnabled}
          forecastRainDay={forecastRainDay}
          setForecastRainDay={setForecastRainDay}
          forecastOptions={forecastOptions}
          datas={datas}
          datasDischarge={datasDischarge}
          onStationSelect={(lat, lng) => {
            if (map) map.setView([lat, lng], 12);
          }}
        />

        {/* Map */}
        <Box
          flex={1}
          style={{
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            zIndex: 0,
          }}
        >

        <FloodForecastMap
          datas={datas}
          datasDischarge={datasDischarge}
          markers={markers}
          location={location}
          zoom={zoom}
          map={map}
          handleSetMap={handleSetMap}
          handleModalOpen={handleModalOpen}
          layers={layers}
          showProvinceBoundary={showProvinceBoundary}
          showAmphoeBoundary={showAmphoeBoundary}
          showTambonBoundary={showTambonBoundary}
          rainfallEnabled={rainfallEnabled}
          rainfallType={rainfallType}
          dataRainfall24={dataRainfall24}
          datasRainToday={datasRainToday}
          datasRainYesterday={datasRainYesterday}
          datasRain3day={datasRain3day}
          datasRain7day={datasRain7day}
          datasRain15Day={datasRain15Day}
          sidebarOpen={sidebarOpen}
          onOpenSidebar={() => setSidebarOpen(true)}
          forecastRainEnabled={forecastRainEnabled}
          forecastRainDay={forecastRainDay}
          handleSetMap={(m) => setMap(m)}
          datasRain30Day={datasRain30Day}
        />
        </Box>
      </Box>

      {/* Modal */}
      <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth container={() => document.fullscreenElement || document.body} style={{ zIndex: 999999 }}>
        <DialogTitle>
          {modalContent.header}
          <IconButton
            onClick={handleModalClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {modalContent.content}
        </DialogContent>
      </Dialog>
    </Box>
  );
}